import {Component, OnInit, OnDestroy, Optional, ChangeDetectorRef} from '@angular/core';
import {Subscription} from 'rxjs';
import {DragulaService} from 'ng2-dragula';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {LayoutService} from '@ngmedax/layout';
import {
  MasterDataLayoutService,
  MasterDataLayout,
  DEFAULT_MASTER_DATA_LAYOUT,
  GridSize,
  MasterDataFieldPosition,
  MasterDataFieldType,
  StandardFieldDefinition,
  CustomFieldDefinition
} from '@ngmedax/master-data';

import {SettingsClientCustomService} from '../../services/settings-client-custom.service';
import {TRANSLATION_MASTER_DATA_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {ClientCustomFieldDefinition} from '../../../../types';


/**
 * Bag name for dragula.
 */
const DRAG_BAG = 'master-data-fields';

/**
 * Available grid size presets.
 */
const GRID_PRESETS: GridSize[] = [
  {cols: 4, rows: 4},
  {cols: 5, rows: 5},
  {cols: 5, rows: 6},
  {cols: 5, rows: 7},
  {cols: 5, rows: 8},
  {cols: 6, rows: 6},
  {cols: 8, rows: 8},
  {cols: 10, rows: 10},
  {cols: 12, rows: 12}
];

/**
 * Counter for generating unique HR element keys.
 */
let hrCounter = 0;

// hack to inject decorator declarations
export interface MasterDataLayoutEditorComponent extends Translatable {}

@Component({
  selector: 'app-master-data-layout-editor',
  templateUrl: './master-data-layout-editor.component.html',
  styleUrls: ['./master-data-layout-editor.component.css']
})
@Translatable({scope: TRANSLATION_MASTER_DATA_SCOPE, keys: KEYS})
export class MasterDataLayoutEditorComponent implements OnInit, OnDestroy {
  /**
   * Grid size presets for dropdown.
   */
  public gridPresets = GRID_PRESETS;

  /**
   * Current grid size.
   */
  public gridSize: GridSize = {cols: 6, rows: 6};

  /**
   * Field positions in the grid.
   */
  public positions: MasterDataFieldPosition[] = [];

  /**
   * Standard field definitions.
   */
  public standardDefinitions: StandardFieldDefinition[] = [];

  /**
   * Custom field definitions.
   */
  public customDefinitions: ClientCustomFieldDefinition[] = [];

  /**
   * Is currently saving.
   */
  public isSaving = false;

  /**
   * Indicates if there are unsaved changes.
   */
  public hasUnsavedChanges = false;

  /**
   * Search term for filtering available fields.
   */
  public fieldSearchTerm = '';

  /**
   * Shows the patient form instead of the layout grid.
   */
  public previewMode = false;

  /**
   * Field that is being moved and the cell area it would land on.
   */
  public movingField: MasterDataFieldPosition | null = null;
  public moveTarget: {col: number, row: number, valid: boolean} | null = null;

  /**
   * Pointer start, grid geometry and grab offset of a move.
   */
  private moveStart: {x: number, y: number, left: number, top: number, colStep: number, rowStep: number,
    grabCol: number, grabRow: number, position: MasterDataFieldPosition} | null = null;

  /**
   * Currently resizing field.
   */
  private resizingField: MasterDataFieldPosition | null = null;

  /**
   * Resize start position.
   */
  private resizeStart: {x: number, y: number, colSpan: number, rowSpan: number, colStep: number, rowStep: number} | null = null;

  /**
   * Subscriptions.
   */
  private subscriptions: Subscription[] = [];

  public constructor(
    @Optional() private translationService: TranslationService,
    private layoutService: LayoutService,
    public masterDataLayoutService: MasterDataLayoutService,
    private customFieldService: SettingsClientCustomService,
    private dragulaService: DragulaService,
    private ref: ChangeDetectorRef
  ) {
    this.setupDragula();
  }

  public async ngOnInit() {
    await this.loadDefinitions();
    await this.loadLayout();
  }

  public ngOnDestroy() {
    document.removeEventListener('mousemove', this.onMoveMove);
    document.removeEventListener('mouseup', this.onMoveEnd);

    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    this.subscriptions = [];

    if (this.dragulaService.find(DRAG_BAG)) {
      this.dragulaService.destroy(DRAG_BAG);
    }
  }

  /**
   * Gets available standard fields (not yet placed in grid).
   */
  public get availableStandardFields(): StandardFieldDefinition[] {
    const placedKeys = this.positions
      .filter(p => p.fieldType === 'standard')
      .map(p => p.fieldKey);
    return this.standardDefinitions.filter(d => !placedKeys.includes(d.key));
  }

  /**
   * Gets filtered standard fields based on search term.
   */
  public get filteredStandardFields(): StandardFieldDefinition[] {
    if (!this.fieldSearchTerm.trim()) {
      return this.availableStandardFields;
    }
    const term = this.fieldSearchTerm.toLowerCase();
    return this.availableStandardFields.filter(d => {
      const label = this._(KEYS.CRUD[d.labelKey] || d.labelKey).toLowerCase();
      return label.includes(term) || d.key.toLowerCase().includes(term);
    });
  }

  /**
   * Gets available custom fields (not yet placed in grid).
   */
  public get availableCustomFields(): ClientCustomFieldDefinition[] {
    const placedKeys = this.positions
      .filter(p => p.fieldType === 'custom')
      .map(p => p.fieldKey);
    return this.customDefinitions.filter(d => !placedKeys.includes(d.key));
  }

  /**
   * Gets filtered custom fields based on search term.
   */
  public get filteredCustomFields(): ClientCustomFieldDefinition[] {
    if (!this.fieldSearchTerm.trim()) {
      return this.availableCustomFields;
    }
    const term = this.fieldSearchTerm.toLowerCase();
    return this.availableCustomFields.filter(d =>
      d.label.toLowerCase().includes(term) || d.key.toLowerCase().includes(term)
    );
  }

  /**
   * Checks if HR divider matches the search term.
   */
  public get showHrDivider(): boolean {
    if (!this.fieldSearchTerm.trim()) {
      return true;
    }
    const term = this.fieldSearchTerm.toLowerCase();
    const hrLabel = this._(KEYS.MASTER_DATA.HR_DIVIDER).toLowerCase();
    return hrLabel.includes(term) || 'hr'.includes(term) || 'trennlinie'.includes(term) || 'divider'.includes(term);
  }

  /**
   * Gets the field at a specific grid cell.
   */
  public getFieldAt(col: number, row: number): MasterDataFieldPosition | null {
    return this.positions.find(p =>
      col >= p.col && col < p.col + p.colSpan &&
      row >= p.row && row < p.row + p.rowSpan
    ) || null;
  }

  /**
   * Checks if a cell is the start cell of a field.
   */
  public isFieldStart(col: number, row: number): boolean {
    return this.positions.some(p => p.col === col && p.row === row);
  }

  /**
   * Gets the standard field definition by key.
   */
  public getStandardDefinition(key: string): StandardFieldDefinition | undefined {
    return this.standardDefinitions.find(d => d.key === key);
  }

  /**
   * Gets the custom field definition by key.
   */
  public getCustomDefinition(key: string): ClientCustomFieldDefinition | undefined {
    return this.customDefinitions.find(d => d.key === key);
  }

  /**
   * Gets the label for a field position.
   */
  public getFieldLabel(position: MasterDataFieldPosition): string {
    if (position.fieldType === 'hr') {
      return this._(KEYS.MASTER_DATA.HR_DIVIDER);
    }

    if (position.fieldType === 'standard') {
      const def = this.getStandardDefinition(position.fieldKey);
      return def ? this._(KEYS.CRUD[def.labelKey] || def.labelKey) : position.fieldKey;
    }

    const def = this.getCustomDefinition(position.fieldKey);
    return def ? def.label : position.fieldKey;
  }

  /**
   * Handles grid size change.
   */
  public onGridSizeChange(preset: GridSize) {
    this.gridSize = {...preset};
    // remove fields that are outside the new grid
    this.positions = this.positions.filter(p =>
      p.col + p.colSpan <= this.gridSize.cols &&
      p.row + p.rowSpan <= this.gridSize.rows
    );
    this.markDirty();
  }

  /**
   * Handles dropping a field on a grid cell.
   */
  public onDropField(fieldKey: string, fieldType: MasterDataFieldType, col: number, row: number) {
    // check if cell is already occupied
    if (this.getFieldAt(col, row)) {
      return;
    }

    // for non-HR fields, remove existing position for this field
    if (fieldType !== 'hr') {
      this.positions = this.positions.filter(p => !(p.fieldKey === fieldKey && p.fieldType === fieldType));
    }

    // add new position
    this.positions.push({
      fieldKey: fieldType === 'hr' ? `hr_${++hrCounter}` : fieldKey,
      fieldType,
      col,
      row,
      colSpan: fieldType === 'hr' ? Math.min(3, this.gridSize.cols - col) : 1,
      rowSpan: 1
    });

    this.markDirty();
  }

  /**
   * Removes a field from the grid.
   */
  public onRemoveField(position: MasterDataFieldPosition) {
    const idx = this.positions.indexOf(position);
    if (idx > -1) {
      this.positions.splice(idx, 1);
    }
    this.markDirty();
  }

  /**
   * Starts resizing a field.
   */
  public onResizeStart(event: MouseEvent, position: MasterDataFieldPosition) {
    event.preventDefault();
    event.stopPropagation();

    this.resizingField = position;
    this.resizeStart = {
      x: event.clientX,
      y: event.clientY,
      colSpan: position.colSpan,
      rowSpan: position.rowSpan,
      ...this.measureGridSteps(event.target as HTMLElement)
    };

    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.onResizeEnd);
  }

  /**
   * Distance from one grid cell to the next, the grid columns grow with the available width
   */
  private measureGridSteps(target: HTMLElement): {colStep: number, rowStep: number} {
    const grid = target && target.closest('.grid-container') as HTMLElement;
    if (!grid) {
      return {colStep: 60, rowStep: 60};
    }
    const style = getComputedStyle(grid);
    const rect = grid.getBoundingClientRect();
    const colGap = parseFloat(style.columnGap) || 0;
    const rowGap = parseFloat(style.rowGap) || 0;
    const width = rect.width - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0);
    const height = rect.height - (parseFloat(style.paddingTop) || 0) - (parseFloat(style.paddingBottom) || 0);
    return {
      colStep: (width + colGap) / this.gridSize.cols || 60,
      rowStep: (height + rowGap) / this.gridSize.rows || 60
    };
  }

  /**
   * Handles resize move.
   */
  private onResizeMove = (event: MouseEvent) => {
    if (!this.resizingField || !this.resizeStart) {
      return;
    }

    const deltaX = event.clientX - this.resizeStart.x;
    const deltaY = event.clientY - this.resizeStart.y;

    const newColSpan = Math.max(1, Math.min(
      this.resizeStart.colSpan + Math.round(deltaX / this.resizeStart.colStep),
      this.gridSize.cols - this.resizingField.col
    ));

    // HR elements can only span columns, not rows
    const newRowSpan = this.resizingField.fieldType === 'hr' ? 1 : Math.max(1, Math.min(
      this.resizeStart.rowSpan + Math.round(deltaY / this.resizeStart.rowStep),
      this.gridSize.rows - this.resizingField.row
    ));

    // check for collision with other fields
    const canResize = !this.hasCollision(this.resizingField, newColSpan, newRowSpan);

    if (canResize) {
      this.resizingField.colSpan = newColSpan;
      this.resizingField.rowSpan = newRowSpan;
      this.ref.detectChanges();
    }
  };

  /**
   * Ends resizing.
   */
  private onResizeEnd = () => {
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);

    if (this.resizingField) {
      this.markDirty();
    }

    this.resizingField = null;
    this.resizeStart = null;
  };

  /**
   * Checks if resizing would cause a collision.
   */
  private hasCollision(position: MasterDataFieldPosition, newColSpan: number, newRowSpan: number): boolean {
    for (const p of this.positions) {
      if (p === position) {
        continue;
      }

      // check overlap
      const overlapsX = position.col < p.col + p.colSpan && position.col + newColSpan > p.col;
      const overlapsY = position.row < p.row + p.rowSpan && position.row + newRowSpan > p.row;

      if (overlapsX && overlapsY) {
        return true;
      }
    }
    return false;
  }

  /**
   * Whether a field fits at the given cell without leaving the grid or overlapping another field.
   */
  private isAreaFree(position: MasterDataFieldPosition, col: number, row: number): boolean {
    if (col < 0 || row < 0 || col + position.colSpan > this.gridSize.cols || row + position.rowSpan > this.gridSize.rows) {
      return false;
    }
    return !this.positions.some(p => p !== position &&
      col < p.col + p.colSpan && col + position.colSpan > p.col &&
      row < p.row + p.rowSpan && row + position.rowSpan > p.row);
  }

  /**
   * Starts moving a placed field; remove button and resize handle keep their own action.
   */
  public onMoveStart(event: MouseEvent, position: MasterDataFieldPosition) {
    const target = event.target as HTMLElement;
    if (event.button !== 0 || target.closest('.resize-handle, .qa-md-remove')) {
      return;
    }
    event.preventDefault();

    const grid = target.closest('.grid-container') as HTMLElement;
    const steps = this.measureGridSteps(target);
    const rect = grid.getBoundingClientRect();
    const style = getComputedStyle(grid);
    const left = rect.left + (parseFloat(style.paddingLeft) || 0);
    const top = rect.top + (parseFloat(style.paddingTop) || 0);
    this.moveStart = {
      x: event.clientX, y: event.clientY, left, top, ...steps, position,
      grabCol: Math.floor((event.clientX - left) / steps.colStep) - position.col,
      grabRow: Math.floor((event.clientY - top) / steps.rowStep) - position.row
    };

    document.addEventListener('mousemove', this.onMoveMove);
    document.addEventListener('mouseup', this.onMoveEnd);
  }

  /**
   * Follows the pointer with the target area, after a few pixels so a click stays a click.
   */
  private onMoveMove = (event: MouseEvent) => {
    const start = this.moveStart;
    if (!start) {
      return;
    }
    if (!this.movingField && Math.hypot(event.clientX - start.x, event.clientY - start.y) < 4) {
      return;
    }

    const position = start.position;
    const col = Math.max(0, Math.min(this.gridSize.cols - position.colSpan,
      Math.floor((event.clientX - start.left) / start.colStep) - start.grabCol));
    const row = Math.max(0, Math.min(this.gridSize.rows - position.rowSpan,
      Math.floor((event.clientY - start.top) / start.rowStep) - start.grabRow));

    this.movingField = position;
    this.moveTarget = {col, row, valid: this.isAreaFree(position, col, row)};
    this.ref.detectChanges();
  };

  /**
   * Places the field at the target area when it is free.
   */
  private onMoveEnd = () => {
    document.removeEventListener('mousemove', this.onMoveMove);
    document.removeEventListener('mouseup', this.onMoveEnd);

    const position = this.movingField;
    const target = this.moveTarget;
    if (position && target && target.valid && (target.col !== position.col || target.row !== position.row)) {
      position.col = target.col;
      position.row = target.row;
      this.markDirty();
    }

    this.moveStart = null;
    this.movingField = null;
    this.moveTarget = null;
    this.ref.detectChanges();
  };

  /**
   * Resets the layout (removes all fields).
   */
  public onReset() {
    this.positions = [];
    this.markDirty();
  }

  /**
   * Loads the default layout as a template.
   */
  public onLoadDefaultLayout() {
    this.gridSize = {...DEFAULT_MASTER_DATA_LAYOUT.gridSize};
    this.positions = DEFAULT_MASTER_DATA_LAYOUT.positions.map(p => ({...p}));
    this.markDirty();
  }

  /**
   * Saves the layout explicitly.
   */
  public async onSave() {
    await this.saveLayout();
    this.hasUnsavedChanges = false;
    alert(this._(KEYS.MASTER_DATA.SAVE_SUCCESS));
  }

  /**
   * Creates an array of numbers for iteration.
   */
  public range(n: number): number[] {
    return Array.from({length: n}, (_, i) => i);
  }

  /**
   * Gets the display label for a grid size.
   */
  public getGridSizeLabel(size: GridSize): string {
    return `${size.cols} x ${size.rows}`;
  }

  /**
   * Checks if a grid size is currently selected.
   */
  public isGridSizeSelected(size: GridSize): boolean {
    return size.cols === this.gridSize.cols && size.rows === this.gridSize.rows;
  }

  /**
   * Layout the preview renders: the current, also unsaved layout, or the default the patient form falls back to.
   */
  public get previewLayout(): MasterDataLayout {
    return this.masterDataLayoutService.getEffectiveLayout({gridSize: this.gridSize, positions: this.positions});
  }

  /**
   * Placeholder of a standard field in the preview, the same texts as in the patient form
   */
  public getPreviewPlaceholder(position: MasterDataFieldPosition): string {
    const keys: {[fieldKey: string]: string} = {
      firstName: KEYS.CRUD.ENTER_FIRSTNAME, lastName: KEYS.CRUD.ENTER_LASTNAME, location: KEYS.CRUD.ENTER_LOCATION,
      pseudoId: KEYS.CRUD.ENTER_PSEUDO_ID, customerNr: KEYS.CRUD.ENTER_PATIENT_ID, caseNr: KEYS.CRUD.ENTER_CASE_NUMBER,
      status: KEYS.CRUD.ENTER_STATUS
    };
    if (position.fieldKey === 'birthDate') {
      const locale = this.translationService ? this.translationService.getLocale() : 'de_DE';
      return locale === 'de_DE' ? 'DD.MM.YYYY' : 'MM/DD/YYYY';
    }
    return keys[position.fieldKey] ? this._(keys[position.fieldKey]) : this.getFieldLabel(position);
  }

  /**
   * Whether the preview shows the default layout, as no field has been placed yet.
   */
  public get previewLayoutIsDefault(): boolean {
    return !this.masterDataLayoutService.hasCustomLayout({gridSize: this.gridSize, positions: this.positions});
  }

  /**
   * Gets the CSS class for a field type.
   */
  public getFieldTypeClass(position: MasterDataFieldPosition): string {
    return `field-type-${position.fieldType}`;
  }

  /**
   * Sets up dragula for drag & drop.
   */
  private setupDragula() {
    if (this.dragulaService.find(DRAG_BAG)) {
      this.dragulaService.destroy(DRAG_BAG);
    }

    this.dragulaService.createGroup(DRAG_BAG, {
      copy: (el, source) => source?.classList.contains('available-fields-list'),
      accepts: (el, target) => target?.classList.contains('grid-cell') && !target?.classList.contains('occupied'),
      moves: (el, container, handle) => {
        return handle?.classList.contains('drag-handle') || handle?.parentElement?.classList.contains('drag-handle');
      }
    });

    this.subscriptions.push(
      this.dragulaService.drop(DRAG_BAG).subscribe(({el, target}) => {
        if (target && el) {
          const fieldKey = el.getAttribute('data-field-key');
          const fieldType = el.getAttribute('data-field-type') as MasterDataFieldType;
          const col = parseInt(target.getAttribute('data-col') || '0', 10);
          const row = parseInt(target.getAttribute('data-row') || '0', 10);

          if (fieldKey && fieldType) {
            this.onDropField(fieldKey, fieldType, col, row);
          }

          // remove the cloned element that dragula creates
          if (el.parentNode) {
            el.parentNode.removeChild(el);
          }
        }
      })
    );
  }

  /**
   * Loads field definitions.
   */
  private async loadDefinitions() {
    try {
      // load standard field definitions
      this.standardDefinitions = this.masterDataLayoutService.getStandardFieldDefinitions();

      // load custom field definitions
      const result = await this.customFieldService.loadCustomFields();
      this.customDefinitions = result.rows;
    } catch (error) {
      console.error('Failed to load field definitions', error);
      this.customDefinitions = [];
    }
  }

  /**
   * Loads the layout from settings.
   */
  private async loadLayout() {
    try {
      const layout = await this.masterDataLayoutService.loadLayout();

      if (!layout) {
        // no layout configured yet, use defaults
        this.gridSize = {cols: 6, rows: 6};
        this.positions = [];
        return;
      }

      this.gridSize = layout.gridSize;
      this.positions = layout.positions;

      // update hr counter based on existing positions
      const hrPositions = this.positions.filter(p => p.fieldType === 'hr');
      for (const hp of hrPositions) {
        const match = hp.fieldKey.match(/hr_(\d+)/);
        if (match) {
          hrCounter = Math.max(hrCounter, parseInt(match[1], 10));
        }
      }
    } catch (error) {
      console.error('Failed to load layout', error);
    }
  }

  /**
   * Saves the layout to settings.
   */
  private async saveLayout() {
    this.isSaving = true;

    try {
      await this.masterDataLayoutService.saveLayout({
        gridSize: this.gridSize,
        positions: this.positions
      });
    } catch (error) {
      console.error('Failed to save layout', error);
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Marks the layout as having unsaved changes.
   */
  private markDirty() {
    this.hasUnsavedChanges = true;
  }
}

